// A settled NumberCamera pan can reuse a previously completed RGBA8 pixel only
// when both Float64 coordinates fed to the full iterator are bit-identical.
// The X and Y loops are separable, so certification is O(width + height).
// JSON keys can equate NaN with null, although palette arithmetic cannot.
// Admit only the finite scalar inputs used by the built-in palette sampler.
export function canCertifyCpuPalette(params) {
  if (!params || typeof params !== 'object' ||
      (params.style !== 'lagoon' && params.style !== 'classic') ||
      typeof params.useMu !== 'boolean' ||
      typeof params.gScale !== 'number' || !Number.isFinite(params.gScale) ||
      Object.is(params.gScale, -0)) return false;
  for (const key of ['phaseOffset', 'aFixed', 'aScale']) {
    if (params[key] != null &&
        (typeof params[key] !== 'number' || !Number.isFinite(params[key]) ||
          Object.is(params[key], -0))) return false;
  }
  if (params.repeat !== undefined && typeof params.repeat !== 'boolean') return false;
  return true;
}

export function planExactCpuPan(oldRanges, newRanges, width, height) {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) return null;
  const ox = oldRanges?.xRange, oy = oldRanges?.yRange;
  const nx = newRanges?.xRange, ny = newRanges?.yRange;
  if (![ox?.[0], ox?.[1], oy?.[0], oy?.[1], nx?.[0], nx?.[1], ny?.[0], ny?.[1]].every(Number.isFinite)) return null;
  const oldXS = (ox[1] - ox[0]) / width;
  const oldYS = (oy[1] - oy[0]) / height;
  const newXS = (nx[1] - nx[0]) / width;
  const newYS = (ny[1] - ny[0]) / height;
  if (![oldXS, oldYS, newXS, newYS].every(Number.isFinite) ||
      oldXS === 0 || oldYS === 0 || newXS === 0 || newYS === 0) return null;
  const dx = Math.round((nx[0] - ox[0]) / newXS);
  const dy = Math.round((ny[0] - oy[0]) / newYS);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) ||
      Math.abs(dx) >= width || Math.abs(dy) >= height) return null;
  const cleanX = new Uint8Array(width), cleanY = new Uint8Array(height);
  let cleanColumns = 0, cleanRows = 0;
  for (let j = 0; j < width; j++) {
    const oldJ = j + dx;
    if (oldJ < 0 || oldJ >= width) continue;
    const oldC = ox[0] + (oldJ + 0.5) * oldXS;
    const newC = nx[0] + (j + 0.5) * newXS;
    if (Number.isFinite(oldC) && Number.isFinite(newC) && Object.is(oldC, newC)) {
      cleanX[j] = 1;
      cleanColumns++;
    }
  }
  for (let i = 0; i < height; i++) {
    const oldI = i + dy;
    if (oldI < 0 || oldI >= height) continue;
    const oldC = oy[0] + (oldI + 0.5) * oldYS;
    const newC = ny[0] + (i + 0.5) * newYS;
    if (Number.isFinite(oldC) && Number.isFinite(newC) && Object.is(oldC, newC)) {
      cleanY[i] = 1;
      cleanRows++;
    }
  }
  return { dx, dy, cleanX, cleanY, cleanColumns, cleanRows,
    reusablePixels: cleanColumns * cleanRows,
    totalPixels: width * height };
}
